import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, UserCircle2, ArrowRight, AlertTriangle, Info } from "lucide-react";
import { findArticleBySlug, DISEASE_ARTICLES, pickArticleLang } from "../data/diseaseArticles";
import { articleIdFromSlug, articleSlug } from "../data/diseaseSlugs";
import { dict } from "../i18n";
import SEO from "../components/SEO";
import NotFound from "./NotFound";
import { pagePath } from "../lib/routes";

function Block({ block }) {
  if (block.h2) return <h2 className="text-2xl font-semibold text-surface-ink mt-10 mb-4">{block.h2}</h2>;
  if (block.p)
    return (
      <p className="text-[15px] leading-[1.75] text-surface-ink mb-4 whitespace-pre-wrap">{block.p}</p>
    );
  if (block.ul)
    return (
      <ul className="list-disc pl-6 space-y-1.5 text-[15px] leading-[1.7] text-surface-ink mb-4 marker:text-teal-500">
        {block.ul.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  if (block.ol)
    return (
      <ol className="list-decimal pl-6 space-y-1.5 text-[15px] leading-[1.7] text-surface-ink mb-4 marker:text-sapphire-700">
        {block.ol.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
    );
  if (block.callout) {
    const isWarning = block.callout.type === "warning";
    const Icon = isWarning ? AlertTriangle : Info;
    return (
      <div
        className={`my-6 rounded-2xl border p-5 ${
          isWarning
            ? "border-amber-300/60 bg-amber-50/60"
            : "border-teal-300/60 bg-teal-200/15"
        }`}
      >
        <div className="flex items-start gap-3">
          <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${isWarning ? "text-amber-600" : "text-teal-500"}`} />
          <div>
            <div className="text-[14px] font-semibold text-surface-ink mb-1">
              {block.callout.title}
            </div>
            <div className="text-[14px] leading-relaxed text-surface-inkSoft">
              {block.callout.body}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

export default function DiseaseArticle({ lang }) {
  const { slug } = useParams();
  // Slug may be in any language (RU/EN localised). Resolve to canonical id first.
  const canonicalId = articleIdFromSlug(slug) || slug;
  const rawArticle = findArticleBySlug(canonicalId);
  const article = pickArticleLang(rawArticle, lang);
  const t = dict[lang];

  const ui = {
    et: {
      readingMin: "min lugemist", updated: "Uuendatud",
      note: "Märkus:", noteBody: "Käesolev artikkel on informatiivse iseloomuga ega asenda arsti konsultatsiooni. Sümptomite ilmnemisel või ravi muutmiseks pöördu Tallinna Endokrinoloogia Kliiniku endokrinoloogi poole.",
      ctaTag: "Broneeri aeg", ctaTitle: "Vaja personaalset nõu?",
      ctaBody: "Tallinna Endokrinoloogia Kliiniku endokrinoloog ja õed aitavad sind diabeedi juhtimisel — broneeri vastuvõtt veebis, telefonis või e-mailiga.",
      ctaBook: "Broneeri vastuvõtt", ctaContact: "Kirjuta meile",
      related: "Sarnased artiklid", readArticle: "Loe artiklit",
    },
    ru: {
      readingMin: "мин чтения", updated: "Обновлено",
      note: "Примечание:", noteBody: "Данная статья носит информационный характер и не заменяет консультацию врача. При появлении симптомов или для изменения лечения обратитесь к эндокринологу Таллиннской клиники эндокринологии.",
      ctaTag: "Запись на приём", ctaTitle: "Нужен личный совет?",
      ctaBody: "Эндокринолог и медсёстры Таллиннской клиники эндокринологии помогут вам в управлении диабетом — запишитесь онлайн, по телефону или по e-mail.",
      ctaBook: "Записаться на приём", ctaContact: "Написать нам",
      related: "Похожие статьи", readArticle: "Читать статью",
    },
    en: {
      readingMin: "min read", updated: "Updated",
      note: "Note:", noteBody: "This article is for informational purposes only and does not replace a doctor's consultation. If symptoms appear or to change treatment, contact an endocrinologist at Tallinn Endocrinology Clinic.",
      ctaTag: "Book an appointment", ctaTitle: "Need personal advice?",
      ctaBody: "The endocrinologist and nurses at Tallinn Endocrinology Clinic will help you manage diabetes — book online, by phone or by e-mail.",
      ctaBook: "Book an appointment", ctaContact: "Write to us",
      related: "Related articles", readArticle: "Read article",
    },
  }[lang];

  if (!article) {
    // Unknown article slug — render the NotFound page (with noindex meta) so
    // Google drops the URL instead of treating it as a soft 404 that
    // duplicates the /haigused listing page.
    return <NotFound lang={lang} />;
  }

  // If the URL slug is not the canonical for the current language, 301-style
  // redirect to the correct localised slug so Google indexes one URL per lang.
  const expectedSlug = articleSlug(rawArticle.slug, lang);
  if (slug !== expectedSlug) {
    return <Navigate to={`${pagePath("diseases", lang)}/${expectedSlug}`} replace />;
  }

  // Related articles (same category, exclude self) — max 3
  const related = DISEASE_ARTICLES
    .filter((a) => a.category === rawArticle.category && a.slug !== rawArticle.slug)
    .slice(0, 3)
    .map((a) => pickArticleLang(a, lang));

  return (
    <>
      <SEO
        lang={lang}
        page="diseases"
        pathOverride={`/haigused/${rawArticle.slug}`}
        title={`${article.title} — Tallinna Endokrinoloogia Kliinik`}
        description={article.summary.slice(0, 160)}
        articleMeta={{
          title: article.title,
          summary: article.summary,
          image: article.image,
          updated: rawArticle.updated,
        }}
        breadcrumb={[
          { name: t.diseasesPage.title, path: pagePath("diseases", lang) },
          { name: article.title, path: `${pagePath("diseases", lang)}/${expectedSlug}` },
        ]}
      />

      <article className="container-page pt-10 pb-16 max-w-3xl" data-testid={`disease-article-${article.slug}`}>
        <Link
          to={pagePath("diseases", lang)}
          data-testid="disease-back-btn"
          className="inline-flex items-center gap-1.5 text-[14px] text-sapphire-700 hover:gap-2.5 transition-all mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> {t.diseasesPage.tag}
        </Link>

        <div className="chip mb-3">{article.category.toUpperCase()}</div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-surface-ink leading-[1.15] tracking-tight">
          {article.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-surface-inkSoft">
          <span className="inline-flex items-center gap-1.5">
            <UserCircle2 className="h-4 w-4" /> {article.author}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-4 w-4" /> {article.readingMinutes} {ui.readingMin}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-4 w-4" /> {ui.updated} {article.updated}
          </span>
        </div>

        <div className="mt-8 text-[16px] leading-[1.7] text-surface-inkSoft">
          <p className="text-[17px] leading-[1.6] text-surface-ink italic border-l-4 border-teal-500 pl-5">
            {article.summary}
          </p>
        </div>

        {article.image && (
          <figure className="mt-10">
            <div className="rounded-2xl overflow-hidden border border-surface-outline/40 bg-surface-canvas">
              <img
                src={article.image}
                alt={`${article.title}/Tallinna Endokrinoloogia Kliinik`}
                title={article.imageCaption || article.title}
                className="w-full h-auto object-cover"
                width="1376"
                height="768"
                fetchPriority="high"
                decoding="async"
                data-testid={`article-hero-image-${article.slug}`}
              />
            </div>
            {article.imageCaption && (
              <figcaption className="text-center text-[13px] italic text-surface-inkSoft mt-3">
                {article.imageCaption}
              </figcaption>
            )}
          </figure>
        )}

        <div className="mt-10">
          {article.sections.map((block, idx) => (
            <Block key={idx} block={block} />
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 rounded-2xl border border-surface-outline/60 bg-surface-canvas p-5 text-[13px] text-surface-inkSoft leading-relaxed">
          <strong className="text-surface-ink">{ui.note}</strong> {ui.noteBody}
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-sapphire-700 to-[#005a87] text-white p-7">
          <div className="text-[13px] uppercase tracking-wider opacity-80 mb-2">{ui.ctaTag}</div>
          <h3 className="text-2xl font-semibold mb-3">{ui.ctaTitle}</h3>
          <p className="text-[14px] opacity-90 mb-5">{ui.ctaBody}</p>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-book-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-white text-sapphire-700 px-5 py-3 font-medium text-[14px] hover:bg-sapphire-50 transition-colors"
            >
              {ui.ctaBook} <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to={pagePath("contact", lang)}
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-white px-5 py-3 font-medium text-[14px] hover:bg-white/15 transition-colors"
            >
              {ui.ctaContact}
            </Link>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <div className="text-[13px] uppercase tracking-wider text-surface-inkSoft mb-4">
              {ui.related}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`${pagePath("diseases", lang)}/${articleSlug(r.slug, lang)}`}
                  data-testid={`disease-related-${r.slug}`}
                  className="card-clinic group hover:border-sapphire-300 transition-colors"
                >
                  <div className="text-[11px] uppercase tracking-wider text-teal-500 font-semibold mb-2">
                    {r.category}
                  </div>
                  <div className="text-[16px] font-semibold text-surface-ink leading-snug group-hover:text-sapphire-700 transition-colors">
                    {r.title}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-sapphire-700 group-hover:gap-2.5 transition-all">
                    {ui.readArticle} <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
